import React, { useState, FC, Children, CSSProperties, useRef, useCallback, useEffect } from 'react';
import SwiperType, { Autoplay, Navigation, Pagination, Thumbs } from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/scrollbar'; // eslint-disable-line import/no-unresolved
import 'swiper/css/autoplay'; // eslint-disable-line import/no-unresolved
import { NavigationOptions } from 'swiper/types';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import SwiperCore from 'swiper';
import SliderNavigation, { SliderNavigationProps } from './slider-navigation';

export type SliderProps = SliderNavigationProps & {
  allowArrowsOnTouchDevice?: boolean;
  className?: string;
  containerClassName?: string;
  spaceBetween?: number;
  slidesPerView?: number;
  dots?: boolean;
  fitToSlides?: boolean;
  slideWidth?: number;
  withThumbs?: boolean;
  slideWidthIsFlexible?: boolean;
  /**
   * Render the slider in the server HTML instead of hiding it until Swiper has initialised.
   *
   * Off by default, and it should stay off unless the slider has been checked: every slide is
   * stamped with `slideWidth` (300px) in the server render and only gets its real width once
   * Swiper measures the DOM, so a full-bleed slider paints as a row of 300px slivers and then
   * snaps, shifting the page by hundreds of pixels. Only worth turning on where the server paint
   * is already close to the final layout.
   */
  visibleBeforeInit?: boolean;
  /**
   * Autoplay interval in ms, for sliders that want something other than the 5s default.
   *
   * A `delay` inside the `autoplay` prop has never been read — the autoplay options below are
   * rebuilt from scratch — so several callers pass one that does nothing. Setting it here is the
   * only way to change the interval, and it leaves every existing caller on the 5s they actually
   * run at today.
   */
  autoplayDelay?: number;
} & SwiperProps;

const Slider: FC<SliderProps> = ({
  className = '',
  containerClassName = '',
  slideWidth = 300,
  slidesPerView,
  fitToSlides = false,
  arrows = false,
  innerArrows = false,
  solidArrows = false,
  dots = true,
  spaceBetween = 20,
  withThumbs = false,
  children,
  onSwiper,
  onInit,
  allowTouchMove,
  prevButtonStyles = {},
  nextButtonStyles = {},
  prevArrowStyles = {},
  nextArrowStyles = {},
  compactNavigation,
  slideWidthIsFlexible,
  allowArrowsOnTouchDevice = false,
  autoplay,
  autoplayDelay = 5000,
  visibleBeforeInit = false,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  //Drives two things: the reveal above (slides only have their real width once Swiper has measured
  //the DOM, so the slider stays hidden until then unless a caller opts out via `visibleBeforeInit`)
  //and the autoplay effect below - `swiperRef` is assigned imperatively in `onSwiper`, which does
  //not re-render, so flipping this is what re-runs that effect once the instance exists.
  const [init, setInit] = useState(false);

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setTimeout(() => setInit(true));
      onInit?.(swiper);
    },
    [onInit],
  );

  SwiperCore.use([Autoplay]);

  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>();

  const swiperRef = useRef<SwiperType>();

  const validToFit: boolean = !!fitToSlides && !!slideWidth && !!slidesPerView;
  const sliderWidth: CSSProperties['width'] =
    validToFit && slidesPerView ? `${spaceBetween * (slidesPerView - 1) + slideWidth * slidesPerView}px` : '100%';

  const containerClassNames = useClassNames([
    containerClassName,
    'slider_container relative',
    !init && !visibleBeforeInit ? 'hidden' : 'block',
    {
      'slider__container--fit': validToFit,
      'slider__container--with-thumbs': withThumbs,
    },
  ]);
  const slidesClassName = useClassNames(['slider', className]);

  const slideProps = {
    width: slideWidthIsFlexible ? 'fit-content' : `${slideWidth}px`,
  } as React.CSSProperties;

  const slides = Children.map(children, (child) => <SwiperSlide style={slideProps}>{child}</SwiperSlide>);

  const handleOnSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    onSwiper?.(swiper);

    if (withThumbs) {
      setThumbsSwiper(swiper);
    }
  };

  const handleOnBeforeInit = (swiper: SwiperType) => {
    (swiper.params.navigation as NavigationOptions).prevEl = navigationPrevRef.current;
    (swiper.params.navigation as NavigationOptions).nextEl = navigationNextRef.current;
  };

  useEffect(() => {
    const swiperInstance = swiperRef.current;
    if (swiperInstance) {
      const handleAutoplayStart = () => {};
      const handleAutoplayStop = () => {};
      const handleAutoplayPause = () => {};
      const handleAutoplayResume = () => {};
      swiperInstance.on('autoplayStart', handleAutoplayStart);
      swiperInstance.on('autoplayStop', handleAutoplayStop);
      swiperInstance.on('autoplayPause', handleAutoplayPause);
      swiperInstance.on('autoplayResume', handleAutoplayResume);

      if (autoplay === false) {
        swiperInstance.autoplay.stop();
      } else if (swiperInstance.autoplay.running) {
        swiperInstance.autoplay.start();
      } else {
        swiperInstance.autoplay.start();
      }
      return () => {
        swiperInstance.off('autoplayStart', handleAutoplayStart);
        swiperInstance.off('autoplayStop', handleAutoplayStop);
        swiperInstance.off('autoplayPause', handleAutoplayPause);
        swiperInstance.off('autoplayResume', handleAutoplayResume);
      };
    }
  }, [init, autoplay]);

  return (
    <div className={containerClassNames}>
      <Swiper
        key={`${allowTouchMove} ${isTouchDevice}`}
        className={slidesClassName}
        modules={[Navigation, Pagination, Thumbs]}
        thumbs={{ swiper: thumbsSwiper }}
        pagination={dots ? { clickable: true, bulletActiveClass: 'slider__bullet--active' } : false}
        slidesPerView={slidesPerView ?? 'auto'}
        spaceBetween={spaceBetween}
        style={{ width: sliderWidth }}
        autoplay={autoplay ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
        }: false }
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onSwiper={handleOnSwiper}
        onBeforeInit={handleOnBeforeInit}
        observer
        observeParents
        onInit={handleInit}
        allowTouchMove={allowTouchMove && isTouchDevice}
        {...props}
      >
        {slides}
      </Swiper>
      <SliderNavigation
        compactNavigation={compactNavigation}
        arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
        prevButtonStyles={prevButtonStyles}
        nextButtonStyles={nextButtonStyles}
        prevArrowStyles={prevArrowStyles}
        nextArrowStyles={nextArrowStyles}
        navigationPrevRef={navigationPrevRef}
        navigationNextRef={navigationNextRef}
        totalSlides={slides?.length}
        swiperRef={swiperRef?.current}
        innerArrows={innerArrows}
        solidArrows={solidArrows}
      />
    </div>
  );
};

export default Slider;